import { useEffect, useState } from "react";
import axios from "axios";
import { ALL_INTENSIVE, baseURL } from "../../../Api/Api";
import "./Sections.css";

const api = axios.create({ baseURL });

function SkeletonCards() {
  return (
    <div className="loading-grid">
      {[1, 2, 3].map((i) => (
        <div key={i} className="skeleton-card">
          <div className="skeleton-img" />
          <div className="skeleton-body">
            <div className="skeleton-line medium" />
            <div className="skeleton-line short" />
            <div className="skeleton-line short" />
          </div>
        </div>
      ))}
    </div>
  );
}

function parseSchedule(raw) {
  if (!raw) return [];
  try { const p = JSON.parse(raw); if (Array.isArray(p)) return p; } catch {}
  return [];
}

function IntensiveCard({ course }) {
  const imgSrc = course.photo
    ? `${baseURL}/uploads/intensive/${course.photo}`
    : null;

  const schedule = parseSchedule(course.times);

  return (
    <div className="intensive-card">
      <div className="intensive-card-img-wrap">
        {imgSrc ? (
          <img src={imgSrc} alt={course.name} />
        ) : (
          <div className="intensive-card-img-fallback">
            <svg width="60" height="60" fill="white" viewBox="0 0 24 24">
              <path d="M19 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-7 3a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm6 13H6v-.9c0-2 4-3.1 6-3.1s6 1.1 6 3.1V19z" />
            </svg>
          </div>
        )}
        <div className="intensive-card-overlay" />
      </div>

      <div className="intensive-card-body">
        <div className="intensive-card-name" title={course.name}>
          {course.name}
        </div>

        <div className="intensive-card-meta">
          {course.subject_name && (
            <div className="meta-item">
              <span className="meta-icon icon-blue">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zm0 12.45L4.21 11 12 6.55 19.79 11 12 15.45z" />
                </svg>
              </span>
              <span>{course.subject_name}</span>
            </div>
          )}

          {course.class_name && (
            <div className="meta-item">
              <span className="meta-icon icon-green">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z" />
                </svg>
              </span>
              <span>{course.class_name}</span>
            </div>
          )}

          {course.period && (
            <div className="meta-item">
              <span className="meta-icon icon-orange">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm.5 5v5.25l4.5 2.67-.75 1.23L11 13V7z" />
                </svg>
              </span>
              <span>المدة: {course.period}</span>
            </div>
          )}

          {course.start_date && (
            <div className="meta-item">
              <span className="meta-icon icon-blue">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 4h-1V2h-2v2H8V2H6v2H5C3.89 4 3 4.9 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 16H5V9h14v11z" />
                </svg>
              </span>
              <span>يبدأ: {new Date(course.start_date).toLocaleDateString("ar-SY")}</span>
            </div>
          )}

          {schedule.length > 0 && (
            <div className="meta-item meta-item--wrap">
              <span className="meta-icon icon-orange">
                <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" />
                </svg>
              </span>
              <span className="schedule-tags">
                {schedule.map((s, i) => (
                  <span key={i} className="schedule-tag">
                    {s.day}
                    {s.time ? ` ${s.time}` : ""}
                    {s.hours ? ` · ${s.hours}س` : ""}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function IntensiveSection() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(ALL_INTENSIVE)
      .then((res) => setCourses(res.data?.data || []))
      .catch(() => setCourses([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="section-wrapper light" id="courses">
      <div className="container">
        <div className="section-header">
          <span className="section-badge badge-blue">الدورات المكثفة</span>
          <h2 className="section-title">دورات مكثفة متخصصة</h2>
          <p className="section-subtitle">
            تعرف على الدورات التدريبية المتاحة واختر المسار الذي يساعدك في تحقيق أهدافك.
          </p>
          <div className="section-divider divider-blue" />
        </div>

        {loading ? (
          <SkeletonCards />
        ) : courses.length === 0 ? (
          <div className="empty-state">
            <svg width="64" height="64" fill="#9ca3af" viewBox="0 0 24 24">
              <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z" />
            </svg>
            <p>لا توجد دورات متاحة حالياً</p>
          </div>
        ) : (
          <div className="cards-grid">
            {courses.map((course) => (
              <IntensiveCard key={course.id} course={course} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
